// src/pages/conta-corrente-processo/ContaCorrenteProcessoPage.jsx
import React, { useCallback, useEffect, useMemo } from 'react';
import { IconButton, Grid, Menu, MenuItem, Typography } from '@material-ui/core';
import { useHistory, useParams } from 'react-router';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { bindMenu, bindTrigger } from 'material-ui-popup-state';
import {
  CheckCircle,
  Delete as DeleteIcon,
  MoreHoriz as MoreHorizIcon,
  Pageview as PageviewIcon,
  MoneyOff as MoneyOffIcon,
} from '@material-ui/icons';
import QCXFinalTextField from '../../../../shared-components/final-text-field/QCXFinalTextField';
import QCXSimpleConsultPageTemplate from '../../../../templates/simple-consult-page/QCXSimpleConsultPageTemplate';
import {
  LOADING_STATUS,
  ALTERNATIVE_LOADING_STATUS,
  SUCCESS_STATUS,
  // ...
} from '../../../../features/status';

import {
  selectContaCorrenteProcesso,
  selectLoading,
  fetchContaCorrenteProcessoByFollowUp,
  resetContaCorrenteProcesso,
  selectError,
  selectStatus,
  bloquearProcesso,
  selectBloqueado,
} from '../../../../features/conta-corrente-processo/contaCorrenteProcessoSlice';
import QCXInfoAlert from '../../../../shared-components/alert/QCXInfoAlert';
import { setErrorFeedback, setInfoFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import { loading as loadingAction, success as successAction } from '../../../../features/follow-up/followUpSlice';
import { formatDate } from '../../../../utils/general/general-utils';
import { inactivateByIdAsync } from '../../../../features/movimentacao-financeiro/movimentacaoFinanceiroSlice';
import QCXButton from '../../../../shared-components/button/QCXButton';
import { userHasRole } from '../../../../components/perfil/perfil-utils';
import QCXPopupState from '../../../../components/popup-state/QCXPopupState';
import QCXListItemIcon from '../../../../shared-components/list-item-icon/QCXListItemIcon';
import QCXConfirmDialog from '../../../../shared-components/dialog/QCXConfirmDialog';
import useOperationMiddleware from '../../../../utils/hooks/operation/middleware/useOperationMiddleware';
import ContaCorrenteProcessoUtils from '../../../../utils/general/conta-corrente-processo/ContaCorrenteProcessoUtils';
import { contasPagarAPI } from '../../../../features/contas-pagar/contasPagarAPI';

export default function ContaCorrenteProcessoConsultPage({ authInfo = {} }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const status = useSelector(selectStatus); // Adicionar esta linha
  const { followUpId } = useParams();


  const contaCorrente = useSelector(selectContaCorrenteProcesso);
  const isBloqueado = useSelector(selectBloqueado);
  const formInitialValues = useMemo(() => contaCorrente, [contaCorrente]);
  const isLoading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const followUpStatus = useSelector((state) => state.followUp.status);

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '-';
    const number = typeof value === 'number' ? value : parseFloat(value);
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  };

  const handleBloquearProcesso = () => {
    dispatch(bloquearProcesso({ followUpId, bloqueado: !isBloqueado }));
  };

  useEffect(() => {
    let isMounted = true; // Flag de montagem

    if (followUpStatus === LOADING_STATUS || followUpStatus === ALTERNATIVE_LOADING_STATUS) {
      if (status !== LOADING_STATUS && isMounted) {
        dispatch(loadingAction());
      }
    }

    if (followUpStatus === SUCCESS_STATUS && isMounted) {
      dispatch(successAction());
    }

    return () => {
      isMounted = false; // Cleanup ao desmontar
    };
  }, [followUpStatus, status, dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(setInfoFeedback({ message: error }));
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (!followUpId) {
      dispatch(resetContaCorrenteProcesso());
    }
  }, [dispatch, followUpId]);

  useEffect(() => {
    if (followUpId) {
      dispatch(fetchContaCorrenteProcessoByFollowUp(followUpId));
    }
  }, [dispatch, followUpId]);

  const handleConsultarOrigem = useCallback(
    (event, row, popupState) => {
      popupState.close(event);
      event.stopPropagation();

      const rotaOrigem = ContaCorrenteProcessoUtils.resolveRotaOrigem(row, followUpId);

      if (rotaOrigem) {
        history.push(rotaOrigem);
      }
    },
    [history, followUpId]
  );

  const [handleExcluirByMiddleware, operationOfExcluir] = useOperationMiddleware(async (data) => {
    const result = await dispatch(inactivateByIdAsync(data?.id));

    if (inactivateByIdAsync.fulfilled.match(result)) {
      dispatch(setSuccessFeedback({ message: 'Lançamento excluído com sucesso!' }));
      dispatch(fetchContaCorrenteProcessoByFollowUp(followUpId));
    } else {
      const payload = result.payload || {};

      dispatch(setErrorFeedback({ message: payload.message || 'Erro ao excluir lançamento' }));
    }
  }, [dispatch, followUpId]);

  const handleExcluirClick = useCallback(
    (event, row, popupState) => {
      popupState.close(event);
      event.stopPropagation();

      const configureOperation = () => ({
        options: {
          title: 'Confirmar exclusão do lançamento',
          message: `Deseja excluir o lançamento "${row?.item}" da conta corrente do processo?`,
        },
      });

      handleExcluirByMiddleware(configureOperation, {
        id: row?.movimentacaoFinanceiroId ?? row?.id,
      });
    },
    [handleExcluirByMiddleware]
  );

  // Perda de 100% do lançamento, aplicada sempre no título. Quando o lançamento veio de uma
  // solicitação de pagamento, o backend também reflete a perda nela.
  const [handlePerdaByMiddleware, operationOfPerda] = useOperationMiddleware(async (data) => {
    const removendo = data?.removendo;

    try {
      const response = await contasPagarAPI.atualizarGanhoView(data?.tituloId, {
        tipo: 'PERDA',
        percentual: removendo ? 0 : 100,
        valor: 0,
      });

      if (response?.status === 200) {
        dispatch(
          setSuccessFeedback({
            message: removendo ? 'Perda removida com sucesso!' : 'Perda aplicada com sucesso!',
          })
        );
        dispatch(fetchContaCorrenteProcessoByFollowUp(followUpId));
      }
    } catch (erroPerda) {
      dispatch(
        setErrorFeedback({
          message:
            erroPerda?.response?.data ||
            (removendo ? 'Falha ao remover a perda!' : 'Falha ao aplicar a perda!'),
        })
      );
    }
  }, [dispatch, followUpId]);

  const handlePerdaClick = useCallback(
    (event, row, popupState) => {
      popupState.close(event);
      event.stopPropagation();

      const removendo = !!row?.perda;

      const configureOperation = () => ({
        options: {
          title: removendo ? 'Confirmar remoção da perda' : 'Confirmar perda do lançamento',
          message: removendo
            ? `Deseja remover a perda do lançamento "${row?.item}"? Ele volta para o saldo do processo.`
            : `Deseja lançar 100% do valor de "${row?.item}" como perda? O lançamento sai do saldo do processo e passa para o total de perdas.`,
          endMessage: ContaCorrenteProcessoUtils.getObservacaoPerda(row, removendo),
        },
      });

      handlePerdaByMiddleware(configureOperation, {
        tituloId: row?.tituloId,
        removendo,
      });
    },
    [handlePerdaByMiddleware]
  );

  const columns = useMemo(
    () => [
      {
        field: 'item',
        headerName: 'Item',
        flex: 200,
        valueGetter: ({ row }) => row?.item ?? '-',
      },
      {
        field: 'tipo',
        headerName: 'Tipo',
        flex: 100,
        valueGetter: ({ row }) => row?.tipo ?? '-',
      },
      {
        field: 'origem',
        headerName: 'Origem',
        flex: 120,
        valueGetter: ({ row }) => row?.origem ?? '-',
      },
      {
        field: 'nomeUsuario',
        headerName: 'Usuário',
        flex: 120,
        valueGetter: ({ row }) => row?.nomeUsuario ?? '-',
      },
      {
        field: 'contaBancaria',
        headerName: 'Cta Bancária',
        flex: 160,
        valueGetter: ({ row }) => row?.contaBancaria ?? '-',
      },
      {
        field: 'dataLancamento',
        headerName: 'Data Lcto.',
        flex: 130,
        valueGetter: ({ row }) => formatDate(row?.dataLancamento) ?? '-',
      },
      {
        field: 'dataPagamento',
        headerName: 'Data Pagto.',
        flex: 130,
        valueGetter: ({ row }) => formatDate(row?.dataPagamento) ?? '-',
      },
      {
        field: 'fornecedor',
        headerName: 'Fornecedor',
        flex: 150,
        valueGetter: ({ row }) => row?.fornecedor ?? '-',
      },
      {
        field: 'faturado',
        headerName: 'Faturado',
        flex: 70,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: ({ row }) =>
          row.faturado
            ? <CheckCircle style={{ color: 'green' }} />
            : ' ',
      },
      {
        field: 'conciliado',
        headerName: 'Conciliado',
        flex: 70,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: ({ row }) =>
          row.conciliado
            ? <CheckCircle style={{ color: 'green' }} />
            : ' ',
      },
      {
        field: 'cancelado',
        headerName: 'Cancelado',
        flex: 70,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: ({ row }) =>
          row.cancelado
            ? <CheckCircle style={{ color: 'green' }} />
            : ' ',
      },
      {
        field: 'perda',
        headerName: 'Perda',
        flex: 70,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: ({ row }) =>
          row.perda
            ? <CheckCircle style={{ color: 'green' }} />
            : ' ',
      },
      {
        field: 'valor',
        headerName: 'Valor',
        flex: 100,
        valueGetter: ({ row }) => formatCurrency(row?.valor) ?? '0,00',
      },
      {
        field: 'actions',
        headerName: 'Ações',
        headerAlign: 'center',
        flex: 80,
        sortable: false,
        align: 'center',
        renderCell: ({ row }) => {
          const temOrigemParaConsultar = !!ContaCorrenteProcessoUtils.resolveRotaOrigem(row, followUpId);
          const podeExcluir = ContaCorrenteProcessoUtils.isExclusaoHabilitada(row);
          const podePerda = ContaCorrenteProcessoUtils.isPerdaHabilitada(row);
          const removendoPerda = !!row?.perda;

          return (
            <QCXPopupState popupId={`popup-menu-conta-corrente-processo-${row?.id}`}>
              {(popupState) => (
                <>
                  <IconButton
                    key={`btn-more-options-${row?.id}`}
                    name={`btn-more-options-${row?.id}`}
                    {...bindTrigger(popupState)}
                  >
                    <MoreHorizIcon color="secondary" size={20} />
                  </IconButton>
                  <Menu {...bindMenu(popupState)}>
                    <MenuItem
                      disabled={!temOrigemParaConsultar}
                      onClick={(event) => handleConsultarOrigem(event, row, popupState)}
                    >
                      <QCXListItemIcon>
                        <PageviewIcon fontSize="small" color="primary" />
                      </QCXListItemIcon>
                      <Typography variant="inherit" style={{ fontSize: 12 }}>
                        CONSULTAR
                      </Typography>
                    </MenuItem>
                    <MenuItem
                      disabled={!podeExcluir}
                      onClick={(event) => handleExcluirClick(event, row, popupState)}
                    >
                      <QCXListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                      </QCXListItemIcon>
                      <Typography variant="inherit" style={{ fontSize: 12 }}>
                        EXCLUIR
                      </Typography>
                    </MenuItem>
                    <MenuItem
                      disabled={!podePerda}
                      onClick={(event) => handlePerdaClick(event, row, popupState)}
                    >
                      <QCXListItemIcon>
                        <MoneyOffIcon fontSize="small" htmlColor="#FF0000" />
                      </QCXListItemIcon>
                      <Typography variant="inherit" style={{ fontSize: 12 }}>
                        {removendoPerda ? 'REMOVER PERDA' : 'PERDA'}
                      </Typography>
                    </MenuItem>
                  </Menu>
                </>
              )}
            </QCXPopupState>
          );
        },
      },
    ],
    [followUpId, handleConsultarOrigem, handleExcluirClick, handlePerdaClick]
  );

  const handleNovoLancamento = useCallback(
    (event) => {
      event.stopPropagation();

      // dispatch(changeToCreateMode());

      if (followUpId) {
        history.push(`/financeiro/conta-corrente-processo/${followUpId}/novo`);
      }
    },
    [history]
  );

  const controlProps = useMemo(() => ({
    buttonNew: {
      description: 'Criar novo lançamento',
      onClick: handleNovoLancamento,
      disabled: isBloqueado,
    },
  }), [contaCorrente]);


  return (
    <QCXSimpleConsultPageTemplate
      pageProps={{
        title: 'Conta Corrente do Processo',
        icon: null,
        breadcrumbs: [
          { link: { to: '/financeiro/conta-corrente-processo', name: 'Lista de processos' } },
          { text: { name: 'Conta Corrente Processo' }, default: true },
        ],
      }}
      tableProps={{
        columns,
        rows: contaCorrente?.itens || [],
        pageSize: 10,
        pagination: true,
        checkboxSelection: false,
      }}
      controlProps={controlProps}
      tableData={contaCorrente?.itens || []}
      isLoading={isLoading}
      gridBelow
      authInfo={authInfo}
      filterPropGetter={(row) => `${row.item} ${row.fornecedor} ${row.tipo}`}
    >
      <QCXConfirmDialog
        key="confirm-dialog-excluir-lancamento-conta-corrente"
        id="confirm-dialog-excluir-lancamento-conta-corrente"
        open={operationOfExcluir?.active}
        title={operationOfExcluir?.title}
        content={operationOfExcluir?.message}
        endContent={operationOfExcluir?.endMessage}
        onConfirm={operationOfExcluir.confirm}
        onClose={operationOfExcluir?.reset}
      />
      <QCXConfirmDialog
        key="confirm-dialog-perda-lancamento-conta-corrente"
        id="confirm-dialog-perda-lancamento-conta-corrente"
        open={operationOfPerda?.active}
        title={operationOfPerda?.title}
        content={operationOfPerda?.message}
        endContent={operationOfPerda?.endMessage}
        onConfirm={operationOfPerda.confirm}
        onClose={operationOfPerda?.reset}
      />
      <Form
        onSubmit={() => { }}
        initialValues={formInitialValues}
        mutators={{}}
        enableReinitialize={false}
        render={() => (
          <form style={{ marginTop: 20 }}>
            <Grid container spacing={2} alignItems="stretch">
              {/* ── COLUNA ESQUERDA ── */}
              <Grid item xs={12} md={4} container direction="column" spacing={2}>
                <Grid item>
                  <QCXFinalTextField
                    name="numeroProcesso"
                    label="Processo (Follow-up)"
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item>
                  <QCXFinalTextField
                    name="cliente"
                    label="Cliente"
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item >
                  <QCXFinalTextField
                    name="totalPagoCliente"
                    label="Total Pago Cliente"
                    disabled
                    fullWidth
                    format={formatCurrency}
                  />
                </Grid>
                <Grid item >
                  <QCXFinalTextField
                    name="totalPerda"
                    label="Total Perda"
                    disabled
                    fullWidth
                    format={formatCurrency}
                  />
                </Grid>
              </Grid>

              {/* ── COLUNA DO MEIO ── */}
              <Grid item xs={12} md={4} container direction="column" spacing={2}>
                <Grid item >
                  <QCXFinalTextField
                    name="totalDebitos"
                    label="Total Débitos"
                    disabled
                    fullWidth
                    format={formatCurrency}
                  />
                </Grid>
                <Grid item >
                  <QCXFinalTextField
                    name="totalCreditos"
                    label="Total Créditos"
                    disabled
                    fullWidth
                    format={formatCurrency}
                  />
                </Grid>
                <Grid item>
                  <QCXFinalTextField
                    name="faturadosDebitos"
                    label="Débitos Faturados"
                    disabled
                    fullWidth
                    format={formatCurrency}
                  />
                </Grid>
                <Grid item>
                  <QCXFinalTextField
                    name="faturadosCreditos"
                    label="Créditos Faturados"
                    disabled
                    fullWidth
                    format={formatCurrency}
                  />
                </Grid>

              </Grid>

              {/* ── COLUNA DIREITA ── */}
              <Grid item xs={12} md={4} container direction="column" spacing={2}>
                <Grid item >
                  <QCXFinalTextField
                    name="saldoProcesso"
                    label="Saldo"
                    disabled
                    fullWidth
                    format={formatCurrency}
                  />
                </Grid>
                <Grid item>
                  <QCXFinalTextField
                    name="saldoFaturados"
                    label="Saldo Faturado"
                    disabled
                    fullWidth
                    format={formatCurrency}
                  />
                </Grid>

                {isBloqueado && (
                  <Grid item>
                    <QCXInfoAlert style={{ maxHeight: 38 }} severity="info">
                      Este processo está BLOQUEADO para movimentações financeiras.
                    </QCXInfoAlert>
                  </Grid>
                )}
                {!isBloqueado && (
                  <Grid item>
                    <QCXInfoAlert style={{ maxHeight: 38 }} severity="info">
                      Este processo está LIBERADO para movimentações financeiras.
                    </QCXInfoAlert>
                  </Grid>
                )}
                {!isBloqueado && (
                  <Grid item style={{ paddingBottom: 20 }}>
                    <QCXButton
                      onClick={handleBloquearProcesso}
                      variant="contained"
                      color="secondary"
                      fullWidth
                      id="button-bloquear-processo"
                      key="button-bloquear-processo"
                      disabled={!userHasRole(authInfo, 'bloqueio-financeiro-alterar')}
                    >
                      BLOQUEAR FINANCEIRO DO PROCESSO
                    </QCXButton>
                  </Grid>
                )}
                {isBloqueado && (
                  <Grid item style={{ paddingBottom: 20 }}>
                    <QCXButton
                      onClick={handleBloquearProcesso}
                      variant="contained"
                      color="secondary"
                      fullWidth
                      id="button-desbloquear-processo"
                      key="button-desbloquear-processo"
                      disabled={!userHasRole(authInfo, 'bloqueio-financeiro-alterar')}
                    >
                      DESBLOQUEAR FINANCEIRO DO PROCESSO
                    </QCXButton>
                  </Grid>
                )}

              </Grid>
            </Grid>
          </form>
        )}
      />
    </QCXSimpleConsultPageTemplate>
  );
}
